import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Select, Checkbox, message, Spin, Input } from 'antd';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import { getStoreList, StoreInfo, Scene } from 'services/api/dashboard';
import { useAsync } from 'react-use';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { uniqBy } from 'lodash-es';
import { useVirtualList } from 'ahooks';
import Empty from 'components/Empty';
import { DashboardFilterType } from './useFilter';
import { platformAll2Undefined } from '../../utils';
import { cx } from '@emotion/css';
import { CountryFlagIcon } from 'components/CountryFlagIcon';
import { useGetRegionLabel } from 'hooks/business/useRegion';

const Container = styled.div`
  display: flex;
  width: 400px;
  height: 300px;
`;
const Left = styled.div`
  flex-basis: 140px;
  height: 300px;
  overflow-y: auto;
  border-right: 1px solid #f0f2f5;
`;

const Right = styled.div`
  flex-basis: 260px;
  width: 260px;
  height: 260px;
  overflow-x: hidden;
  overflow-y: auto;
  .ant-spin-container {
    overflow-y: auto;
    .list {
      height: 300px;
    }
  }
`;

const Region = styled.div`
  height: 40px;
  padding: 8px 12px;
  .active {
    color: var(--ant-primary-color-active);
  }
`;
const Store = styled.div`
  padding: 8px 16px;
  width: 100%;
  overflow-x: hidden;
  .ant-checkbox-wrapper {
    display: flex;
    width: 100%;
    overflow: hidden;
    span {
      flex: 1;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
    span.ant-checkbox {
      flex: none;
    }
  }
`;

const selectCls = css`
  min-width: 160px;
  max-width: 600px;
  .ant-select-selection-overflow-item + .ant-select-selection-overflow-item {
    margin-left: 4px;
  }
`;
const Tag = styled.div`
  background: #f5f5f5;
  border: 1px solid rgba(231, 231, 231, 1);
  padding: 0 4px;
  display: flex;
  max-width: 150px;
  overflow: hidden;
  align-items: center;
  gap: 4px;
  svg {
    cursor: pointer;
  }
  .name {
    white-space: nowrap;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

interface Props {
  filter: Pick<
    DashboardFilterType,
    'storeInfo' | 'platform' | 'storeType' | 'department' | 'departments'
  >;
  onChange: (value: DashboardFilterType['storeInfo']) => void;
  regions: General.UpperCaseRegion[];
  scene: Scene;
  className?: string;
}

const StoreSelector = (props: Props) => {
  const { filter, onChange, regions, scene, className } = props;
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const [activeRegion, setActiveRegion] = useState<General.UpperCaseRegion | 'ALL'>('ALL');

  const [searchKeyword, setSearchKeyword] = useState('');
  const [storeInfo, setStoreInfo] = useState(filter.storeInfo);

  useEffect(() => {
    setStoreInfo(filter.storeInfo);
  }, [filter.storeInfo]);

  const { value = [], loading: storeListLoading } = useAsync(
    () => fetchStoreList(),
    [activeRegion],
  );

  const storeList = useMemo(() => {
    if (searchKeyword) {
      return value.filter(
        (item) =>
          item.storeName?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
          item.storeNameAlias?.toLowerCase().includes(searchKeyword.toLowerCase()),
      );
    }
    return value;
  }, [searchKeyword, value]);

  const noData = !storeListLoading && !storeList.length;

  const fetchStoreList = useCallback(
    (region = activeRegion): Promise<StoreInfo[]> => {
      const platform = platformAll2Undefined(filter.platform);
      return getStoreList({
        platforms: platform ? [platform] : undefined,
        regions: region === 'ALL' ? undefined : [region],
        storeTypes: filter.storeType,
        departmentId: filter.department?.id,
        departmentIds: filter.departments?.map(({ id }) => id),
        scene,
      })
        .then((res) => res.storeInfos)
        .catch(() => {
          message.error(t('api.getStoreListErr'));
          return [];
        });
    },
    [activeRegion, filter, scene, t],
  );
  const selected = useMemo(() => {
    const { list = [], region = [] } = storeInfo;
    return {
      ids: list.map((x) => x.id),
      region,
    };
  }, [storeInfo]);
  const onRegionSelect = useCallback(
    async (checked: boolean, value: General.UpperCaseRegion | 'ALL') => {
      const { region = [], list = [] } = storeInfo;
      const currentRegionStoreList =
        value === activeRegion ? storeList : await fetchStoreList(value);

      if (value === 'ALL') {
        if (checked) {
          setStoreInfo({
            region: regions,
            list: currentRegionStoreList,
          });
        } else {
          setStoreInfo({
            region: [],
            list: [],
          });
        }
        return;
      }

      const nextList = checked
        ? uniqBy([...list, ...(currentRegionStoreList ?? [])], 'id')
        : list.filter((x) => x.region !== value);
      const nextRegion = checked ? [...region, value] : region.filter((x) => x !== value);

      setStoreInfo({
        region: nextRegion,
        list: nextList,
      });
    },
    [activeRegion, fetchStoreList, regions, storeInfo, storeList],
  );
  const onStoreSelect = useCallback(
    async (checked: boolean, store: StoreInfo) => {
      const nextList = storeInfo.list || [];
      setStoreInfo({
        region: storeInfo.region,
        list: checked
          ? uniqBy([...nextList, store], 'id')
          : nextList.filter((x) => x.id !== store.id),
      });
    },
    [storeInfo],
  );

  const isAllChecked = useMemo(() => {
    return selected.region.length === regions.length;
  }, [selected.region, regions]);

  return (
    <Select
      placeholder={t('ViewDashboard.选择店铺')}
      dropdownMatchSelectWidth={false}
      maxTagCount={1}
      value={selected.ids}
      allowClear
      className={cx(selectCls, className)}
      mode="multiple"
      onClear={() => {
        onChange({});
      }}
      showSearch={false}
      onDropdownVisibleChange={(open) => {
        if (open && !activeRegion) {
          setActiveRegion('ALL');
        }
        if (!open && storeInfo !== filter.storeInfo) {
          onChange(storeInfo);
        }
      }}
      tagRender={({ value }) => {
        const target = storeInfo.list?.find((x) => x.id === value);
        if (!target) {
          return <></>;
        }

        return (
          <Tag>
            {target.region && <CountryFlagIcon region={target.region} />}
            <span className="name"> {target?.storeName}</span>{' '}
          </Tag>
        );
      }}
      dropdownRender={() => (
        <Container>
          <Left>
            <Region key="all">
              <Checkbox
                checked={isAllChecked}
                onChange={(e) => {
                  onRegionSelect(e.target.checked, 'ALL');
                  setActiveRegion('ALL');
                }}
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setActiveRegion('ALL');
                  }}
                  className={activeRegion === 'ALL' ? 'active' : ''}
                >
                  {t('department.全部店铺')}
                </div>
              </Checkbox>
            </Region>
            {regions.map((region) => (
              <Region
                key={region}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Checkbox
                  checked={selected.region.includes(region)}
                  onChange={(e) => {
                    onRegionSelect(e.target.checked, region);
                    setActiveRegion(region);
                  }}
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setActiveRegion(region);
                    }}
                    className={activeRegion === region ? 'active' : ''}
                  >
                    {getRegionLabel(region)}
                  </div>
                </Checkbox>
              </Region>
            ))}
          </Left>
          <Spin spinning={storeListLoading}>
            <div className="px-[11px] py-2">
              <Input
                size="small"
                placeholder={t('WhatsApp.搜索')}
                value={searchKeyword}
                onChange={(e) => {
                  setSearchKeyword(e.target.value);
                }}
              />
            </div>
            {noData ? (
              <Right>
                <Empty />
              </Right>
            ) : (
              <VirtualList
                storeList={storeList}
                activeRegion={activeRegion}
                selected={selected}
                onStoreSelect={onStoreSelect}
              />
            )}
          </Spin>
        </Container>
      )}
    />
  );
};

const VirtualList = ({
  storeList,
  activeRegion,
  selected,
  onStoreSelect,
}: {
  storeList: StoreInfo[];
  activeRegion: string;
  selected: { ids: string[] };
  onStoreSelect: (checked: boolean, store: StoreInfo) => void;
}) => {
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const { config } = useStoreNameConfig();

  const [vList] = useVirtualList(storeList, {
    containerTarget: containerRef,
    wrapperTarget: wrapperRef,
    overscan: 10,
    itemHeight: 38,
  });

  const nameDisplay = (item: StoreInfo) => {
    return isStoreAliasFirst(config, item.platform, item.region) && item.storeNameAlias
      ? item.storeNameAlias
      : item.storeName || '';
  };

  return (
    <Right key={activeRegion} ref={containerRef}>
      <div ref={wrapperRef}>
        {vList?.map(({ data }) => (
          <Store key={data.id}>
            <Checkbox
              checked={selected.ids.includes(data.id)}
              onChange={(e) => {
                onStoreSelect(e.target.checked, data);
              }}
            >
              {nameDisplay(data)}
            </Checkbox>
          </Store>
        ))}
      </div>
    </Right>
  );
};

export default StoreSelector;
